'use client';

import * as React from 'react';
import { useState, useCallback, useMemo, startTransition, useEffect } from 'react';
import { X, ArrowRight } from '@phosphor-icons/react';
import {
  CartIcon,
  CheckIcon,
  MinusIcon,
  PlusIcon,
  SparkleIcon,
  SpinnerIcon,
  TicketIcon,
} from '@/components/icons';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { ProductThumbImage } from '@/components/features/products/product-thumb-image';
import {
  useCartStore,
  selectCartItems,
  selectCartTotal,
  selectCartItemsCount,
  selectHasHydrated,
  selectPromoEligiblePrice,
  type CartItem,
} from '@/stores/cart-store';
import { useCheckoutStore } from '@/stores/checkout-store';
import { useAddressStore } from '@/stores/address-store';
import { apiClient } from '@/lib/api';
import { getProductById, type ProductWithCategories } from '@/lib/api/products';
import { safeGetItem, safeSetItem, safeRemoveItem } from '@/lib/safe-storage';

const formatPrice = (price: number): string => {
  return Math.round(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₽';
};

// ========== MINI CART ITEM ==========

const MiniCartItem = React.memo(({
  item,
  onUpdateQuantity,
}: {
  item: CartItem;
  onUpdateQuantity: (id: number, qty: number) => void;
}) => {
  return (
    <div className="flex items-center gap-2.5 p-2 rounded-card bg-white/10">
      {/* 40×40: браузер обрежет любой радиус ≥ 20 до окружности, поэтому здесь
          честнее pill — на экране это круг, а не карточка. */}
      <div className="relative w-10 h-10 rounded-pill overflow-hidden flex-shrink-0 bg-white/10">
        <ProductThumbImage product={item.product} sizes="40px" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium text-white truncate leading-tight">
          {item.product.name}
        </p>
        <p className="text-[10px] font-semibold text-white/50 font-mono leading-tight mt-0.5">
          {formatPrice(item.product.price * item.quantity)}
        </p>
      </div>
      <div className="flex items-center rounded-pill overflow-hidden bg-white/15 flex-shrink-0">
        <button
          onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
          className="p-1 text-white hover:bg-white/10 transition-colors"
        >
          {item.quantity === 1 ? (
            <X weight="bold" className="w-2.5 h-2.5 text-white/50" />
          ) : (
            <MinusIcon weight="bold" className="w-2.5 h-2.5" />
          )}
        </button>
        <span className="w-4 text-center text-[10px] font-bold text-white">
          {item.quantity}
        </span>
        <button
          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
          className="p-1 text-white hover:bg-white/10 transition-colors"
        >
          <PlusIcon weight="bold" className="w-2.5 h-2.5" />
        </button>
      </div>
    </div>
  );
});
MiniCartItem.displayName = 'MiniCartItem';

// ========== FLOATING MINI CART ==========

export function FloatingMiniCart() {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();

  useEffect(() => { setMounted(true); }, []);

  const isDark = mounted ? resolvedTheme === 'dark' : false;

  // Cart store
  const hasHydrated = useCartStore(selectHasHydrated);
  const items = useCartStore(selectCartItems);
  const cartTotal = useCartStore(selectCartTotal);
  const cartCount = useCartStore(selectCartItemsCount);
  const removeItem = useCartStore((s) => s.removeItem);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const addPromoItem = useCartStore((s) => s.addPromoItem);
  const removePromoItem = useCartStore((s) => s.removePromoItem);
  const promoEligiblePrice = useCartStore(selectPromoEligiblePrice);

  // Checkout store
  const openCheckout = useCheckoutStore((s) => s.openCheckout);

  // Promo code state
  const [promoInput, setPromoInput] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoApplied, setPromoApplied] = useState<string | null>(null);

  useEffect(() => {
    if (!hasHydrated) return;
    const promoItem = items.find(item => item.bonusType === 'promo');
    if (promoItem) {
      setPromoApplied(safeGetItem('appliedPromoCode') || 'Применён');
    } else {
      setPromoApplied(null);
    }
  }, [items, hasHydrated]);

  const displayCount = useMemo(() => hasHydrated ? cartCount : 0, [hasHydrated, cartCount]);
  const displayTotal = useMemo(() => hasHydrated ? cartTotal : 0, [hasHydrated, cartTotal]);
  const displayItems = useMemo(() => hasHydrated ? items : [], [hasHydrated, items]);

  // Only regular items in mini cart
  const regularItems = useMemo(() =>
    displayItems.filter(item => !item.isBonus && !item.bonusType),
    [displayItems]
  );

  const handleUpdateQuantity = useCallback((id: number, qty: number) => {
    startTransition(() => {
      if (qty <= 0) removeItem(id);
      else updateQuantity(id, qty);
    });
  }, [removeItem, updateQuantity]);

  const handleApplyPromo = useCallback(async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    setPromoError(null);
    try {
      const response = await apiClient.post<{
        id: number; code: string; name: string; product_id: number;
        price: number; is_paid: boolean;
        min_order_sum: number; product_name?: string; product_image?: string;
      }>('/promo-codes/validate', { code: promoInput.trim().toUpperCase() });

      if (response.min_order_sum && promoEligiblePrice < response.min_order_sum) {
        setPromoError(`Мин. сумма ${formatPrice(response.min_order_sum)}`);
        return;
      }

      let product: ProductWithCategories;
      try {
        product = await getProductById(response.product_id);
      } catch {
        product = {
          id: response.product_id,
          name: response.product_name || 'Подарок',
          description: '', price: 0,
          image_url: response.product_image || '',
          categories: [], is_available: true, status: 'available', spiciness: 'none',
          sku: '',
        } as ProductWithCategories;
      }

      // Цену диктует промокод, а не каталог — см. promo-code-input.tsx
      product = { ...product, price: Number(response.price) || 0 };

      addPromoItem(product);
      safeSetItem('appliedPromoCode', response.code);
      safeSetItem('appliedPromoName', response.name);
      setPromoApplied(response.code);
      setPromoInput('');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setPromoError(e?.response?.data?.error || 'Промокод не найден');
    } finally {
      setPromoLoading(false);
    }
  }, [promoInput, promoEligiblePrice, addPromoItem]);

  const handleRemovePromo = useCallback(() => {
    removePromoItem();
    safeRemoveItem('appliedPromoCode');
    safeRemoveItem('appliedPromoName');
    setPromoApplied(null);
    setPromoInput('');
    setPromoError(null);
  }, [removePromoItem]);

  const handleOpenCheckout = useCallback(() => {
    setIsOpen(false);
    startTransition(() => { openCheckout(); });
  }, [openCheckout]);

  // На мобильных мини-корзина — единственный вход в корзину: кнопка в шапке
  // скрыта (hidden md:block), а панель раскрывается локальным стейтом, минуя
  // openCheckout. Поэтому гейт адреса дублируется здесь, иначе на телефоне
  // его можно было бы обойти, просто развернув панель.
  const handleToggle = useCallback(() => {
    if (!isOpen && !useAddressStore.getState().selectedAddress) {
      // Тост заменён окном с объяснением: карта открывается уже из него.
      useAddressStore.getState().openRequiredPrompt('mini_cart');
      // source отличает мобильный отвал от десктопного: гейтов два,
      // и вести они себя будут по-разному.
      import('@/lib/source-tracker').then(({ captureEvent }) => {
        captureEvent('address_gate_blocked', { reason: 'no_address', source: 'mini_cart' });
      }).catch(() => { /* ignore */ });
      return;
    }
    setIsOpen((v) => !v);
  }, [isOpen]);
  const handleClose = useCallback(() => { setIsOpen(false); }, []);

  const isEmpty = regularItems.length === 0;

  // Lock body scroll when mini cart is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Bounce effect on cart icon when count changes
  const [cartBounce, setCartBounce] = useState(false);
  const prevCartCount = React.useRef(displayCount);
  useEffect(() => {
    if (displayCount !== prevCartCount.current && displayCount > 0) {
      setCartBounce(true);
      const t = setTimeout(() => setCartBounce(false), 300);
      prevCartCount.current = displayCount;
      return () => clearTimeout(t);
    }
    prevCartCount.current = displayCount;
  }, [displayCount]);

  return (
    <>
      <div className="fixed bottom-5 right-3 left-3 z-40 md:hidden pointer-events-none">
        <div
          className={cn(
            'ml-auto pointer-events-auto relative',
            'overflow-hidden',
            'transition-[width,border-radius,box-shadow,transform] duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]',
            // Обе ступени свелись в card, поэтому border-radius в переходе
            // ниже больше ничего не анимирует — ширина анимируется как прежде.
            isOpen
              ? 'w-full rounded-card shadow-deep'
              : 'w-[105px] rounded-card shadow-float',
          )}
          style={{
            transform: !isOpen && cartBounce ? 'scale(1.08)' : 'scale(1)',
            transition: cartBounce
              ? 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1), width 500ms cubic-bezier(0.32,0.72,0,1), border-radius 500ms cubic-bezier(0.32,0.72,0,1), box-shadow 500ms cubic-bezier(0.32,0.72,0,1)'
              : undefined,
          }}
        >
          {/* Background layer */}
          <div className={cn(
            'absolute inset-0 rounded-[inherit]',
            !mounted
              ? 'bg-gradient-to-r from-[var(--violet-400)] to-[var(--primary)]'
              : isDark
                ? 'bg-[var(--bg-primary)] border border-white/[0.06]'
                : 'bg-[var(--primary)]'
          )} />
          {mounted && !isDark && (
            <div
              className="absolute inset-0 rounded-[inherit] pointer-events-none"
              style={{
                background: 'linear-gradient(135deg, var(--bonus-platinum), var(--primary))',
              }}
            />
          )}

          {/* === Expandable panel === */}
          <div className={cn(
            'grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]',
            isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}>
            <div className="overflow-y-auto overscroll-contain" style={{ maxHeight: 'calc(100dvh - 140px)' }}>
              <div className={cn(
                'mx-3 h-px bg-white/10 transition-opacity duration-300',
                isOpen ? 'opacity-100 delay-200' : 'opacity-0'
              )} />

              {/* Scrollable items */}
              <div className={cn(
                'px-3 pt-2 pb-1 space-y-1.5',
                'transition-opacity duration-300',
                isOpen ? 'opacity-100 delay-150' : 'opacity-0'
              )}>
                {isEmpty ? (
                  <div className={cn(
                    'flex flex-col items-center justify-center py-6 gap-2',
                    'transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]',
                    isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
                  )}>
                    <CartIcon weight="thin" className="w-8 h-8 text-white/30" />
                    <p className="text-[12px] text-white/50 font-medium">Корзина пуста</p>
                    <p className="text-[10px] text-white/30 text-center leading-snug">
                      Добавьте что-нибудь вкусное
                    </p>
                  </div>
                ) : (
                  regularItems.map((item, i) => (
                    <div
                      key={`${item.id}-r`}
                      className={cn(
                        'transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]',
                        isOpen
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-0 translate-y-3'
                      )}
                      style={{ transitionDelay: isOpen ? `${100 + Math.min(i, 6) * 40}ms` : '0ms' }}
                    >
                      <MiniCartItem
                        item={item}
                        onUpdateQuantity={handleUpdateQuantity}
                      />
                    </div>
                  ))
                )}
              </div>

              {/* First order hint */}
              {!isEmpty && (
              <div className={cn(
                'mx-3 mb-1.5',
                'transition-all duration-400',
                isOpen ? 'opacity-100 translate-y-0 delay-200' : 'opacity-0 translate-y-2'
              )}>
                <div className="flex items-center gap-2 px-3 py-2 rounded-card bg-white/10">
                  <SparkleIcon weight="fill" className="w-3.5 h-3.5 text-white/70 flex-shrink-0" />
                  <p className="text-[10px] text-white/70 leading-snug">
                    Ролл за первый заказ — на 2-м шаге оформления
                  </p>
                </div>
              </div>
              )}

              {/* Promo code */}
              {!isEmpty && (
              <div className={cn(
                'px-3 pb-1.5',
                'transition-all duration-400 ease-[cubic-bezier(0.32,0.72,0,1)]',
                isOpen ? 'opacity-100 translate-y-0 delay-150' : 'opacity-0 translate-y-2'
              )}>
                {promoApplied ? (
                  <div className="flex items-center justify-between p-2 rounded-card bg-white/10">
                    <div className="flex items-center gap-1.5">
                      <TicketIcon weight="fill" className="w-3.5 h-3.5 text-white" />
                      <span className="text-[11px] font-semibold text-white">{promoApplied}</span>
                      <span className="text-[10px] text-white/40">применён</span>
                    </div>
                    <button
                      onClick={handleRemovePromo}
                      className="p-1 rounded-pill text-white/30 hover:text-white/60 hover:bg-white/10 transition-colors"
                    >
                      <X weight="bold" className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex gap-1.5">
                      <input
                        type="text"
                        inputMode="text"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="characters"
                        value={promoInput}
                        onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleApplyPromo(); }}
                        placeholder="Промокод"
                        disabled={promoLoading}
                        className={cn(
                          'flex-1 h-8 px-2.5 rounded-card text-[16px] sm:text-[13px] font-medium',
                          'bg-white/10 text-white placeholder:text-white/25',
                          'focus:outline-none focus:ring-1 focus:ring-white/20',
                          'disabled:opacity-50',
                          promoError && 'ring-1 ring-white/40'
                        )}
                      />
                      <button
                        onClick={handleApplyPromo}
                        disabled={promoLoading || !promoInput.trim()}
                        className={cn(
                          'h-8 px-3 rounded-card text-[11px] font-semibold',
                          'bg-white text-[var(--primary)]',
                          'hover:bg-white/90 active:scale-[0.97]',
                          'disabled:opacity-30 disabled:cursor-not-allowed',
                          'transition-all duration-150 flex items-center gap-1'
                        )}
                      >
                        {promoLoading ? (
                          <SpinnerIcon weight="bold" className="w-3 h-3 animate-spin" />
                        ) : (
                          <CheckIcon weight="bold" className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                    {promoError && (
                      <p className="text-[10px] text-white/60 mt-1 px-0.5">{promoError}</p>
                    )}
                  </div>
                )}
              </div>
              )}

              <div className={cn(
                'px-3 pb-2.5',
                'transition-all duration-400 ease-[cubic-bezier(0.32,0.72,0,1)]',
                isOpen
                  ? 'opacity-100 translate-y-0 delay-200'
                  : 'opacity-0 translate-y-2'
              )}>
                {isEmpty ? (
                  <button
                    onClick={handleClose}
                    className={cn(
                      'w-full flex items-center justify-center gap-2 h-10 rounded-card',
                      'bg-white/10 text-white/50 font-semibold text-[13px]',
                      'hover:bg-white/15 active:scale-[0.97] active:transition-none',
                      'transition-[background-color,transform] duration-150'
                    )}
                  >
                    <span>Свернуть</span>
                  </button>
                ) : (
                  <button
                    onClick={handleOpenCheckout}
                    className={cn(
                      'w-full flex items-center justify-center gap-2 h-10 rounded-card',
                      'bg-white text-[var(--primary)] font-semibold text-[13px]',
                      'hover:bg-white/90 active:scale-[0.97] active:transition-none',
                      'transition-[background-color,transform] duration-150'
                    )}
                  >
                    <span>Оформить</span>
                    <span className="font-mono">{formatPrice(displayTotal)}</span>
                    <ArrowRight weight="bold" className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* === Toggle button === */}
          <button
            onClick={handleToggle}
            className={cn(
              'relative flex items-center justify-center w-full px-3',
              'active:scale-[0.97] active:transition-none',
              'transition-[height] duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]',
              'h-11',
            )}
          >
            <div className="flex items-center gap-1.5">
              <div className={cn(
                'transition-transform duration-400 ease-[cubic-bezier(0.32,0.72,0,1)]',
                isOpen ? 'rotate-90 scale-90' : 'rotate-0 scale-100'
              )}>
                {isOpen ? (
                  <X className="h-4 w-4 text-white" weight="bold" />
                ) : (
                  <div className="relative mr-0.5">
                    <CartIcon className="h-4 w-4 text-white" weight="fill" />
                    {!isEmpty && (
                      <span className="absolute -top-1.5 -right-2 min-w-[14px] h-[14px] flex items-center justify-center rounded-pill bg-white text-[var(--primary)] text-[8px] font-bold px-0.5 leading-none">
                        {displayCount > 99 ? '99' : displayCount}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <span className={cn(
                'text-[13px] font-bold text-white font-mono whitespace-nowrap',
                'transition-all duration-400 ease-[cubic-bezier(0.32,0.72,0,1)]',
                isOpen ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'
              )}>
                {isEmpty ? 'Пусто' : formatPrice(displayTotal)}
              </span>
              <span className={cn(
                'text-[13px] font-semibold text-white whitespace-nowrap',
                'transition-all duration-300',
                isOpen ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'
              )}>
                Свернуть
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-30 md:hidden',
          'bg-black/20',
          'transition-opacity duration-400 ease-out',
          isOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        )}
        onClick={handleClose}
      />
    </>
  );
}
